// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useEffect, useState } from 'react';

import { useDebouncedValue } from '@mantine/hooks';

type Props = {
  /** A model URN or a pre-signed URL for an attachment. */
  url: string;
  children: (url: string) => ReactNode;
};

/**
 * Used with the ForgeViewer components.
 *
 * Delays rendering by one cycle and debounces url changes,
 * giving a previous viewer instance time to fully unmount.
 *
 * Renders nothing until the url is stable, then calls `children`
 * with the stable url.
 */
export const DeferredForgeViewer = ({ url, children }: Props) => {
  const [shouldRender, setShouldRender] = useState(false);
  useEffect(() => {
    setShouldRender(true);
  }, []);

  const [cachedUrl] = useDebouncedValue(url, 1);

  if (cachedUrl !== url || !shouldRender) return null;
  return children(cachedUrl);
};
